/**
 * usePersistedState Hook
 * A generic hook for persisting state to localStorage with type safety.
 * Supports boolean, string, number, and object types.
 */

import { useState, useCallback } from 'react';

type JsonValue = boolean | string | number | object | null;

/**
 * Hook that persists state to localStorage.
 * @param key - localStorage key
 * @param defaultValue - default value if nothing stored
 * @returns [state, setState] tuple
 */
// Overload for boolean to ensure proper type inference
export function usePersistedState(
  key: string,
  defaultValue: boolean
): [boolean, (value: boolean) => void];
// Generic overload for other types
export function usePersistedState<T extends JsonValue>(
  key: string,
  defaultValue: T
): [T, (value: T) => void];
// Implementation
export function usePersistedState<T extends JsonValue>(
  key: string,
  defaultValue: T
): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const saved = localStorage.getItem(key);
      return saved !== null ? JSON.parse(saved) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setPersistedState = useCallback((value: T) => {
    setState(value);
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Ignore localStorage errors (quota exceeded, private browsing, etc.)
    }
  }, [key]);

  return [state, setPersistedState];
}
